import toFixedValue, { toFixedValueInt } from 'app/common/lib/toFixedValue'
import { ReportAverageCheckData } from 'app/dataTypes/reports/data/common/averageCheck'

import { Entity } from './types'

type AverageCheckItem = ReportAverageCheckData['orgsTotalByDateIndex'][number]
type CellValue = 'revenueByDay' | 'salesCountByDay' | 'averageCheck'

type Cell = {
  columnKey: string
  value: string
  type?: 'isTotalTitle'
}

const rowTitles: Record<CellValue, string> = {
  revenueByDay: 'Выручка',
  salesCountByDay: 'Количество продаж',
  averageCheck: 'Средний чек',
}

const formatValue = (valueName: CellValue, value: number) => {
  return valueName === 'salesCountByDay' ? toFixedValueInt(value) : toFixedValue(value)
}

const createRowsForItem = ({
  title,
  rowKeyPrefix,
  item,
  dataByDateIndex,
  columnKeysMain,
  isTotal = false,
}: {
  title: string
  rowKeyPrefix: string
  item: AverageCheckItem
  dataByDateIndex: Array<AverageCheckItem>
  columnKeysMain: Array<string>
  isTotal?: boolean
}) => {
  // строка-заголовок (название организации), остальные ячейки пустые
  const titleRow = {
    rowKey: `orgTitle_${rowKeyPrefix}`,
    rowData: columnKeysMain.reduce(
      (acc, key) => {
        acc[key] = { columnKey: key, value: '' }
        return acc
      },
      {
        [Entity.Title]: {
          columnKey: Entity.Title as const,
          value: title,
          ...(isTotal ? { type: 'isTotalTitle' as const } : {}),
        },
        [Entity.Total]: { columnKey: Entity.Total as const, value: '' },
      } as Record<string, Cell>,
    ),
  }

  // строки с данными по датам: выручка, количество продаж, средний чек
  const valueRows = (['revenueByDay', 'salesCountByDay', 'averageCheck'] as Array<CellValue>).map(
    (valueName) => ({
      rowKey: `${rowKeyPrefix}_${valueName}`,
      rowData: columnKeysMain.reduce(
        (acc, key, dateIndex) => {
          const value = dataByDateIndex[dateIndex]
          acc[key] = {
            columnKey: key,
            value: value ? formatValue(valueName, value[valueName]) : '',
          }
          return acc
        },
        {
          [Entity.Title]: {
            columnKey: Entity.Title as const,
            value: rowTitles[valueName],
          },
          [Entity.Total]: {
            columnKey: Entity.Total as const,
            value: formatValue(valueName, item[valueName]),
          },
        } as Record<string, Cell>,
      ),
    }),
  )

  return [titleRow, ...valueRows]
}

export const createData = ({
  orgs,
  columnKeysMain,
}: {
  orgs: ReportAverageCheckData['orgs']
  columnKeysMain: Array<string>
}) => {
  return orgs.flatMap(({ id, title, dataByDateIndex, total }) =>
    createRowsForItem({
      title,
      rowKeyPrefix: id,
      item: total,
      dataByDateIndex,
      columnKeysMain,
    }),
  )
}

export const createDataTotal = ({
  orgsTotalByDateIndex,
  totalOverall,
  columnKeysMain,
}: {
  orgsTotalByDateIndex: ReportAverageCheckData['orgsTotalByDateIndex']
  totalOverall: ReportAverageCheckData['totalOverall']
  columnKeysMain: Array<string>
}) => {
  return createRowsForItem({
    title: 'Итого',
    rowKeyPrefix: 'total',
    item: totalOverall,
    dataByDateIndex: orgsTotalByDateIndex,
    columnKeysMain,
    isTotal: true,
  })
}
